import { isSendStep } from "@/utils/isSendStep";

import { ListPerson } from "../../../List/schema/list";
import {
	resolveStepFailure,
	StepFailure,
} from "../../../List/components/CampaignOverview/components/Lead/utils/resolveStepFailure";
import { getCurrentCategory } from "../../pages/Outbox/outbox-category.helper";

/** The web-side Outbox status buckets (mirrors the API's OutboxTab vocabulary). */
export type OutboxBucket = "scheduled" | "inProgress" | "ended" | "paused" | "error";

type Schedule = NonNullable<ListPerson["schedules"]>[number];

/**
 * The lead's steps that send something — condition, starter and visit nodes and the `n99-cond`
 * invite gate are not steps of the sequence the card counts (PREC-4157).
 */
const sendSchedules = (row: ListPerson): Schedule[] => (row.schedules ?? []).filter(isSendStep);

export const countSentSteps = (row: ListPerson): number =>
	sendSchedules(row).filter((s) => s.processingState === "done" && !s.canceledAt).length;

const SENT_UNKNOWN = "sent-unknown";
const FINISHED_ASSIGN_STATUSES = ["ENDED", "MESSAGE_SENT", "DONE"];
const isFinishedStatus = (row: ListPerson): boolean =>
	FINISHED_ASSIGN_STATUSES.includes(row.assignStatus as string);

const SERVER_TAB_TO_BUCKET: Record<string, OutboxBucket> = {
	SCHEDULED: "scheduled",
	IN_PROGRESS: "inProgress",
	ENDED: "ended",
	PAUSED: "paused",
	ERROR: "error",
};

/**
 * Classifies a lead into its Outbox status bucket. Prefers the server-computed
 * `outboxTab` field (the same SQL CASE the tab filter uses); until every row carries
 * it, falls back to mapping the client-side `getCurrentCategory` divider bucket —
 * never a third re-implementation of the classifier.
 */
export const resolveOutboxBucket = (row: ListPerson, todayStart: Date): OutboxBucket => {
	const serverTab = (row as ListPerson & { outboxTab?: string | null }).outboxTab;

	if (serverTab && SERVER_TAB_TO_BUCKET[serverTab]) {
		return SERVER_TAB_TO_BUCKET[serverTab];
	}

	if (resolveStepFailure(row)) {
		return "error";
	}

	const category = getCurrentCategory(row, todayStart) ?? "";

	if (category === "paused") {
		return "paused";
	}

	// "sent-unknown" is also the divider's catch-all for a lead with nothing sent; like the server's
	// CASE, only a finished assignStatus makes that lead ended (PREC-4157).
	if (category.startsWith("sent-") && (category !== SENT_UNKNOWN || isFinishedStatus(row))) {
		return "ended";
	}

	// Scheduled-flavored categories (today/tomorrow/date/next week/later/assigned):
	// with something already sent the sequence is mid-flight, otherwise not started.
	return countSentSteps(row) > 0 ? "inProgress" : "scheduled";
};

export type ChipTone = "success" | "warning" | "error" | "muted" | "progress";

export interface StatusChipDescriptor {
	labelKey: string;
	tone: ChipTone;
}

/**
 * The card's status chip for the non-error buckets. The error bucket renders the
 * existing `StepFailureTag` (with its category label + tooltip) instead of a plain chip.
 */
export const getOutboxStatusChip = (bucket: OutboxBucket): StatusChipDescriptor | null => {
	switch (bucket) {
		case "paused":
			return { labelKey: "outbox.statusChips.paused", tone: "warning" };
		case "ended":
			return { labelKey: "outbox.statusChips.completed", tone: "success" };
		case "inProgress":
			return { labelKey: "outbox.statusChips.inProgress", tone: "progress" };
		case "scheduled":
			return { labelKey: "outbox.statusChips.notStarted", tone: "muted" };
		default:
			return null;
	}
};

export type StepSegmentState = "done" | "error" | "current" | "paused" | "wait";

export interface StepProgressModel {
	segments: StepSegmentState[];
	labelKey: "outbox.steps.stepOf" | "outbox.steps.sentOf";
	labelParams: { current?: number; sent?: number; total: number };
}

export { resolveStepFailure };
export type { StepFailure };

/**
 * The card's step-progress bar: one segment per send step of the sequence, and a
 * "Step X of N" / "N of N sent" label. Null for leads without a scheduled sequence.
 */
export const getStepProgress = (
	row: ListPerson,
	bucket: OutboxBucket
): StepProgressModel | null => {
	const steps = sendSchedules(row);

	if (steps.length === 0) {
		return null;
	}

	const total = steps.length;
	const done = countSentSteps(row);
	let currentMarked = false;

	const segments = steps.map<StepSegmentState>((step) => {
		if (step.processingState === "done" && !step.canceledAt) {
			return "done";
		}

		if (step.failedAt && !step.canceledAt) {
			return "error";
		}

		if (bucket === "paused") {
			return "paused";
		}

		if (!currentMarked && (bucket === "scheduled" || bucket === "inProgress")) {
			currentMarked = true;

			return "current";
		}

		return "wait";
	});

	if (bucket === "ended") {
		return {
			segments,
			labelKey: "outbox.steps.sentOf",
			labelParams: { sent: done, total },
		};
	}

	return {
		segments,
		labelKey: "outbox.steps.stepOf",
		labelParams: { current: Math.min(done + 1, total), total },
	};
};
